import type { ReactNode } from 'react';

/**
 * A base de todo ícone do app.
 *
 * ## Por que ícone é SVG e não caractere
 *
 * O app inteiro desenhava ícone com caractere Unicode — `✕` para fechar, `‹` para voltar, `⌕`
 * para buscar. Funciona **por acidente**: depende de a fonte do aparelho ter aquele glifo. Quando
 * não tem, o sistema desenha uma caixinha com "?" e o botão vira um enigma.
 *
 * Foi o que aconteceu no iPhone com dois deles, e nenhum era o que parecia: `⌕` é U+2315
 * **TELEPHONE RECORDER**, escolhido por lembrar uma lupa em algumas fontes, e `✎` é um dingbat de
 * lápis. Nenhum dos dois existe na fonte do iOS.
 *
 * ## Os desenhos
 *
 * A maioria dos `path` vem do **Lucide** (https://lucide.dev), sob licença ISC:
 *
 *   Copyright (c) 2026 Lucide Icons and Contributors
 *
 * Copiados para cá em vez de instalados: quinze ícones não pagam uma dependência, e assim cada
 * desenho é um arquivo que se lê e se altera.
 *
 * O `ClapIcon` é a exceção: o Lucide não tem palmas, e o desenho dele foi fornecido de fora. A
 * procedência está registrada no próprio arquivo do ícone.
 *
 * ## O traço
 *
 * `strokeWidth` 1.6, e não os 2 que o Lucide usa por padrão. É a espessura que os ícones da barra
 * de navegação (`nav/`) já tinham, transcritos do canvas — e como a espessura é atributo do
 * `<svg>` e não do `path`, alinhar as duas famílias é um número, não um redesenho.
 *
 * O `ClapIcon` não obedece a esse número: sendo preenchimento e não traço, a espessura dele está
 * no desenho, e ele é de peso cheio enquanto todo o resto é linha. Se este 1,6 mudar um dia, ele
 * é o único que terá de ser redesenhado em vez de reconfigurado.
 */
export interface IconProps {
  /** Lado do quadrado, em px. O viewBox é sempre 24×24. */
  size?: number;
  className?: string;
}

export function Icon({
  size = 20,
  className,
  children,
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
      className={className}
      /*
       * Decorativo por padrão, e é o padrão certo: todo botão que só tem ícone neste app já
       * carrega `aria-label`, e um `<title>` aqui daria ao leitor de tela um segundo nome para a
       * mesma coisa.
       */
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
